import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import SearchFilters from './components/SearchFilters';
import ThinClientFormModal from './components/ThinClientFormModal';
import ThinClientDetailModal from './components/ThinClientDetailModal';
import ConfirmModal from './components/ConfirmModal';
import { mockThinClients, mockTenants, mockVenues } from './mockData';
import type { ThinClient } from './types';

export default function ThinClientsPage() {
  const [clients, setClients] = useState<ThinClient[]>(mockThinClients);
  const [filteredClients, setFilteredClients] = useState<ThinClient[]>(mockThinClients);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ThinClient | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: string; client: ThinClient | null }>({ type: '', client: null });

  const handleSearch = (filters: any) => {
    let filtered = [...clients];

    if (filters.searchValue) {
      filtered = filtered.filter(client => {
        const value = filters.searchValue.toLowerCase();
        switch (filters.searchType) {
          case 'mac':
            return client.macWireless.toLowerCase().includes(value) || 
                   client.macWired.toLowerCase().includes(value);
          case 'name':
            return client.alias.toLowerCase().includes(value);
          case 'sn':
            return client.serialNumber.toLowerCase().includes(value);
          case 'id':
            return client.id.toLowerCase().includes(value);
          default:
            return true;
        }
      });
    }

    if (filters.tenant) {
      filtered = filtered.filter(client => client.tenantId === filters.tenant);
    }

    if (filters.venue) {
      filtered = filtered.filter(client => client.venueId === filters.venue);
    }

    if (filters.status) {
      filtered = filtered.filter(client => client.status === filters.status);
    }

    if (filters.startDate) {
      filtered = filtered.filter(client => {
        const clientDate = new Date(client.createdAt).toISOString().split('T')[0];
        return clientDate >= filters.startDate;
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(client => {
        const clientDate = new Date(client.createdAt).toISOString().split('T')[0];
        return clientDate <= filters.endDate;
      });
    }

    setFilteredClients(filtered);
  };

  const handleAdd = () => {
    setSelectedClient(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (client: ThinClient) => {
    setSelectedClient(client);
    setIsFormModalOpen(true);
  };

  const handleViewDetail = (client: ThinClient) => {
    setSelectedClient(client);
    setIsDetailModalOpen(true);
  };

  const handleToggleStatus = (client: ThinClient) => {
    setConfirmAction({ type: 'toggle', client });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = (client: ThinClient) => {
    setConfirmAction({ type: 'delete', client });
    setIsConfirmModalOpen(true);
  };

  const handleFormSubmit = (formData: any) => {
    const tenant = mockTenants.find(t => t.id === formData.tenantId);
    const venue = mockVenues.find(v => v.id === formData.venueId);
    
    if (selectedClient) {
      const updatedClients = clients.map(c => 
        c.id === selectedClient.id 
          ? {
              ...c,
              alias: formData.alias,
              serialNumber: formData.serialNumber,
              status: formData.status,
              tenantId: formData.tenantId,
              tenant: tenant?.name || '',
              venueId: formData.venueId,
              venue: venue?.name || '',
              macWireless: formData.macWireless,
              macWired: formData.macWired,
              firmwareVersion: formData.firmwareVersion,
              updatedAt: new Date().toLocaleString('zh-CN', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false 
              }).replace(/\//g, '-'),
              updatedBy: '当前用户'
            }
          : c
      );
      setClients(updatedClients);
      setFilteredClients(updatedClients);
      alert('硬件终端更新成功！');
    } else {
      const newClient: ThinClient = {
        id: `TC${String(clients.length + 1).padStart(3, '0')}`,
        alias: formData.alias,
        serialNumber: formData.serialNumber,
        status: formData.status,
        tenantId: formData.tenantId,
        tenant: tenant?.name || '',
        venueId: formData.venueId,
        venue: venue?.name || '',
        macWireless: formData.macWireless,
        macWired: formData.macWired,
        firmwareVersion: formData.firmwareVersion,
        lastBootTime: '-',
        createdAt: new Date().toLocaleString('zh-CN', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        }).replace(/\//g, '-'),
        createdBy: '当前用户',
        updatedAt: new Date().toLocaleString('zh-CN', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        }).replace(/\//g, '-'),
        updatedBy: '当前用户'
      };
      const updatedClients = [...clients, newClient];
      setClients(updatedClients);
      setFilteredClients(updatedClients);
      alert('硬件终端创建成功！');
    }
    setIsFormModalOpen(false);
  };

  const handleConfirm = () => {
    if (confirmAction.type === 'toggle' && confirmAction.client) {
      const newStatus = confirmAction.client.status === 'enabled' ? 'disabled' : 'enabled';
      const updatedClients = clients.map(c =>
        c.id === confirmAction.client!.id
          ? { 
              ...c, 
              status: newStatus,
              updatedAt: new Date().toLocaleString('zh-CN', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false 
              }).replace(/\//g, '-'),
              updatedBy: '当前用户'
            }
          : c
      );
      setClients(updatedClients);
      setFilteredClients(updatedClients);
      alert(`硬件终端已${newStatus === 'enabled' ? '启用' : '停用'}！`);
      setIsDetailModalOpen(false);
    } else if (confirmAction.type === 'delete' && confirmAction.client) {
      const updatedClients = clients.filter(c => c.id !== confirmAction.client!.id);
      setClients(updatedClients);
      setFilteredClients(updatedClients);
      alert('硬件终端已删除！');
      setIsDetailModalOpen(false);
    }
    setIsConfirmModalOpen(false);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">硬件终端</h1>
          <p className="text-sm text-gray-600 mt-1">完成对云盒的管理，平台管理员统一管理，租户可查看</p>
        </div>

        <SearchFilters 
          onSearch={handleSearch}
          tenants={mockTenants}
          venues={mockVenues}
        />

        <div className="bg-white rounded-lg">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="text-sm text-gray-600">
              共 {filteredClients.length} 条记录
            </div>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              新建终端
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">终端别名</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">序列号SN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">场所</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">租户</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">无线MAC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">有线MAC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">最近开机时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">固件版本</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">创建时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">创建人</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">当前登录用户</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">当前连接主机</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{client.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{client.alias}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.serialNumber}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(client)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          client.status === 'enabled' ? 'bg-teal-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            client.status === 'enabled' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{client.venue}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{client.tenant}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.macWireless}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.macWired}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.lastBootTime}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.firmwareVersion}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.createdAt}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.createdBy}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{client.currentUser || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {client.currentHost ? `${client.currentHost.id} - ${client.currentHost.name}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(client)}
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="编辑"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleViewDetail(client)}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="详情"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        <button
                          onClick={() => handleToggleStatus(client)}
                          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                            client.status === 'enabled'
                              ? 'text-orange-600 hover:bg-orange-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={client.status === 'enabled' ? '停用' : '启用'}
                        >
                          <i className={client.status === 'enabled' ? 'ri-pause-circle-line' : 'ri-play-circle-line'}></i>
                        </button>
                        <button
                          onClick={() => handleDelete(client)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="删除"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ThinClientFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleFormSubmit}
        client={selectedClient}
        tenants={mockTenants}
        venues={mockVenues}
      />

      <ThinClientDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        client={selectedClient}
        onEdit={() => {
          setIsDetailModalOpen(false);
          setIsFormModalOpen(true);
        }}
        onToggleStatus={() => {
          if (selectedClient) {
            handleToggleStatus(selectedClient);
          }
        }}
        onDelete={() => {
          if (selectedClient) {
            handleDelete(selectedClient);
          }
        }}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirm}
        title={
          confirmAction.type === 'delete' 
            ? '删除硬件终端' 
            : confirmAction.client?.status === 'enabled' 
              ? '停用硬件终端' 
              : '启用硬件终端'
        }
        message={
          confirmAction.type === 'delete'
            ? `确定要删除硬件终端"${confirmAction.client?.alias}"吗？此操作不可恢复。`
            : `确定要${confirmAction.client?.status === 'enabled' ? '停用' : '启用'}硬件终端"${confirmAction.client?.alias}"吗？`
        }
        type={confirmAction.type === 'delete' ? 'danger' : 'warning'}
      />
    </MainLayout>
  );
}
